import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import Button from './base/Button';
import Icon, { IconName, IconTheme } from './base/Icon';
import Text from './base/Text';
import { Div, StyledUtilsProps } from './helpers/StyledUtils';
import { MODAL_WIDTHS } from '@hookdeck/theme';

const widths = {
  small: `${MODAL_WIDTHS.small}px`,
  medium: `${MODAL_WIDTHS.medium}px`,
  large: `${MODAL_WIDTHS.large}px`,
  full: '100%',
};

interface Props extends StyledUtilsProps {
  title?: string;
  title_icon?: IconName;
  icon_theme?: IconTheme;
  subtitle?: string;
  portal?: boolean;
  cancel_label?: string;
  submit_label?: string;
  submit_icon?: IconName;
  hide_footer?: boolean;
  size?: keyof typeof widths | 'full';
  cancel?: string;
  onClose: () => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  is_submitting?: boolean;
  submit_disabled?: boolean;
  danger?: boolean;
  hide_close?: boolean;
  footer_content?: React.ReactNode;
  alt_action?: {
    icon?: IconName;
    label?: string;
    to?: string;
    onClick?: () => void;
  };
  handle_click_outside?: () => void;
  z_index?: number;
  card_radius?: 'normal' | 'large';
}

export const StyledModalBackdrop = styled.div<{ size: keyof typeof widths; z_index?: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.surface.base.backdrop};
  z-index: ${({ theme, z_index }) => (z_index ? z_index - 1 : theme.zindex.modal_backdrop)};

  ${({ size, theme }) =>
    size === 'full'
      ? css`
          ${StyledModalSection}:first-of-type {
            flex-grow: 1;
          }
          ${StyledModalSection}:first-of-type, ${StyledModalSection}:last-of-type {
            margin: 0 !important;

            border-radius: 0 !important;
          }
        `
      : css`
          padding: 0 ${theme.spacing(6)};
        `}
`;

export const StyledModalScreen = styled.div(
  ({ theme }) => css`
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: ${theme.colors.surface.base.surface};
    z-index: ${theme.zindex.screen_modal};

    form {
      height: 100%;
    }
  `,
);

const StyledModalSection = styled(Div)`
  background-color: ${({ theme }) => theme.colors.surface.base.surface};
  border-bottom: ${({ theme }) => theme.border};

  &:last-of-type {
    border-bottom: none;
  }
`;

const StyledModalCard = styled.div<{
  size: keyof typeof widths;
  z_index?: number;
  $card_radius: 'normal' | 'large';
}>`
  width: 100%;
  max-width: ${({ size }) => widths[size] || widths.medium};
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(6)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  margin-left: auto;
  margin-right: auto;
  flex-direction: column;
  height: ${({ size }) => (size === 'full' ? '100vh' : 'fit-content')};
  max-height: 90vh;
  z-index: ${({ theme, z_index }) => z_index || theme.zindex.modal};
  box-shadow: ${({ theme }) => theme.elevation[3]};
  border-radius: ${({ theme, $card_radius }) => theme.radius[$card_radius]};
  overflow: hidden;

  ${StyledModalSection} {
    &:first-of-type,
    &:nth-of-type(2):not(:last-of-type) {
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
`;

const ModalContainer: React.FC<PropsWithChildren<{ portal?: boolean }>> = ({
  children,
  portal,
}) => {
  const element = (() => {
    if (!document.getElementById('app-modal')) {
      const div = document.createElement('div');
      div.id = 'app-modal';
      document.body.appendChild(div);
    }
    return document.getElementById('app-modal') as HTMLElement;
  })();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'visible';
    };
  }, []);

  if (portal) {
    return createPortal(children, element);
  }
  return <>{children}</>;
};

export const ScreenModal: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <ModalContainer portal>
      <StyledModalScreen>{children}</StyledModalScreen>
    </ModalContainer>
  );
};

const Modal: React.FC<PropsWithChildren<Props>> = ({
  title,
  title_icon,
  icon_theme,
  subtitle,
  portal,
  cancel_label,
  submit_label,
  submit_icon,
  hide_footer,
  footer_content,
  children,
  size = 'medium',
  danger = false,
  submit_disabled = false,
  onSubmit,
  onClose,
  onCancel,
  is_submitting,
  hide_close = false,
  alt_action,
  handle_click_outside,
  z_index,
  card_radius = 'large',
  ...props
}) => {
  const modalRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      event.stopPropagation();
      if (
        event.button === 0 &&
        modalRef.current &&
        !event.composedPath().includes(modalRef.current)
      ) {
        onClose();
      }
    };
    if (size !== 'full') {
      document.addEventListener('mousedown', handle_click_outside || handleClickOutside);
    }

    return () => {
      if (size !== 'full') {
        document.removeEventListener('mousedown', handle_click_outside || handleClickOutside);
      }
    };
  }, [onClose, size]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const primary_or_danger = danger ? { danger: true } : { primary: true };

  return (
    <ModalContainer portal={!!portal}>
      <StyledModalBackdrop size={size} z_index={z_index}>
        <StyledModalCard
          id="modal"
          ref={modalRef}
          size={size}
          z_index={z_index}
          $card_radius={card_radius}>
          {title && (
            <StyledModalSection p={{ x: 4, y: !hide_close ? 2.5 : 3 }}>
              <Div flex={{ justify: 'space-between', align: 'center' }}>
                <Text bold size="l">
                  {title}
                  {title_icon && <Icon icon={title_icon} right {...icon_theme} />}
                </Text>
                {!hide_close && <Button ghost onClick={onClose} icon="close" />}
              </Div>
              {subtitle && (
                <Text muted m={{ b: 2 }}>
                  {subtitle}
                </Text>
              )}
            </StyledModalSection>
          )}
          <StyledModalSection style={{ overflow: 'auto' }} p={4} {...props}>
            {children}
          </StyledModalSection>
          {(onCancel || submit_label || alt_action) && !hide_footer && (
            <StyledModalSection flex={{ justify: 'space-between', gap: 3 }} p={{ x: 4, y: 3 }}>
              <Div>{footer_content}</Div>
              <Div flex={{ gap: 3 }}>
                {alt_action && (
                  <Div w={100} m={{ l: 'auto' }}>
                    <Button
                      outline
                      onClick={alt_action.onClick}
                      to={alt_action.to}
                      icon={alt_action.icon}>
                      {alt_action.label}
                    </Button>
                  </Div>
                )}
                {onCancel && cancel_label && (
                  <Div flex={{ align: 'center' }}>
                    <Button disabled={is_submitting} outline onClick={onCancel}>
                      {cancel_label}
                    </Button>
                  </Div>
                )}
                {submit_label && (
                  <Div flex={{ align: 'center' }}>
                    <Button.Permission
                      data-testid="modal-submit"
                      disabled={is_submitting || submit_disabled}
                      {...primary_or_danger}
                      submit
                      onClick={onSubmit || undefined}
                      icon={is_submitting ? 'loading' : submit_icon || undefined}>
                      {submit_label}
                    </Button.Permission>
                  </Div>
                )}
              </Div>
            </StyledModalSection>
          )}
        </StyledModalCard>
      </StyledModalBackdrop>
    </ModalContainer>
  );
};

export default Modal;
